import React, { useEffect, useMemo, useState } from 'react';
import { submitPackageEnquiry } from '../../api/packagesListing';
import { useToast } from '../ToastContext';
import { useCurrency } from '../CurrencyContext';
import { getLocalItem, setLocalItem } from '../../utils/safeStorage';

const STORAGE_KEY = 'homevilas_package_enquiry_form_v2';
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const baseForm = {
  fullName: '',
  email: '',
  phone: '',
  travelDate: '',
  travellers: '',
  budget: '',
  message: '',
};

const normalizeInitialValues = (values = {}) => ({
  fullName: values.fullName || values.name || '',
  email: values.email || '',
  phone: values.phone || '',
  travelDate: values.travelDate || values.travel_date || values.travelStartDate || '',
  travellers: values.travellers || values.travellersCount || '',
  budget: values.budget || values.budgetMax || values.budgetMin || '',
  message: values.message || '',
});

const fieldClassName =
  'mt-2 w-full rounded-2xl border border-[#d8deeb] bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-[#0f766e] focus:ring-4 focus:ring-[#0f766e]/10';

const PackageEnquiryForm = ({
  pkg,
  source = 'DETAILS',
  initialValues = {},
  onSuccess,
  onCancel,
  labels = {},
  compact = false,
}) => {
  const toast = useToast();
  const { formatCurrency } = useCurrency();
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(() => {
    let stored = {};
    const raw = getLocalItem(STORAGE_KEY);
    if (raw) {
      try {
        stored = JSON.parse(raw) || {};
      } catch (_) {
        stored = {};
      }
    }
    return { ...baseForm, ...stored, ...normalizeInitialValues(initialValues) };
  });

  useEffect(() => {
    setForm((prev) => ({ ...prev, ...normalizeInitialValues(initialValues) }));
  }, [initialValues]);

  useEffect(() => {
    setLocalItem(STORAGE_KEY, JSON.stringify(form));
  }, [form]);

  const destinationLabel = pkg?.destination_name || pkg?.destination_city || pkg?.destination_label || 'Selected destination';
  const priceValue = pkg?.price_from || pkg?.price_per_person || 0;
  const priceLabel = formatCurrency ? formatCurrency(priceValue, pkg?.currency || 'INR') : priceValue;

  const packageFacts = useMemo(
    () => [
      { label: 'Package', value: pkg?.title || 'Selected package' },
      { label: 'Destination', value: destinationLabel },
      {
        label: 'Starting price',
        value: priceValue ? `${priceLabel} ${labels.perPerson || 'per person'}` : 'On request',
      },
    ],
    [destinationLabel, labels.perPerson, pkg?.title, priceLabel, priceValue]
  );

  const packageSummaryClass = compact
    ? 'mt-3 space-y-3'
    : 'mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3';

  const packageSummaryItemClass = compact
    ? 'rounded-[22px] border border-[#e2e8f3] bg-white px-4 py-4 shadow-[0_10px_28px_rgba(15,23,42,0.05)]'
    : 'rounded-2xl border border-white/80 bg-white px-4 py-3 shadow-sm';

  const packageSummaryValueClass = compact
    ? 'mt-2 text-base font-semibold leading-7 text-slate-900'
    : 'mt-1 break-words text-sm font-semibold leading-6 text-slate-900';

  const validate = () => {
    const nextErrors = {};
    const normalizedName = form.fullName.trim();
    const normalizedEmail = form.email.trim();
    const normalizedPhone = form.phone.replace(/[^\d+]/g, '');

    if (!pkg?.id) nextErrors.package = 'Please select a package.';
    if (!normalizedName) nextErrors.fullName = 'Full name is required.';
    if (!normalizedEmail) nextErrors.email = 'Email is required.';
    else if (!emailPattern.test(normalizedEmail)) nextErrors.email = 'Enter a valid email address.';
    if (!normalizedPhone) nextErrors.phone = 'Phone number is required.';
    else if (normalizedPhone.replace(/\D/g, '').length < 7) nextErrors.phone = 'Enter a valid phone number.';

    if (form.travellers && Number(form.travellers) < 1) {
      nextErrors.travellers = 'Travellers must be at least 1.';
    }

    if (form.budget && Number(form.budget) < 0) {
      nextErrors.budget = 'Budget must be a valid number.';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined, package: undefined }));
  };

  const submit = async (event) => {
    event?.preventDefault?.();
    if (!validate()) return;

    setSubmitting(true);
    try {
      await submitPackageEnquiry({
        packageId: pkg.id,
        packageTitle: pkg.title,
        fullName: form.fullName,
        email: form.email.trim(),
        phone: form.phone.trim(),
        travelDate: form.travelDate || undefined,
        travellers: form.travellers ? Number(form.travellers) : undefined,
        budget: form.budget ? Number(form.budget) : undefined,
        message: form.message.trim() || undefined,
        source,
      });
      toast.success('Enquiry submitted successfully.');
      setForm((prev) => ({
        ...prev,
        travelDate: '',
        travellers: '',
        budget: '',
        message: '',
      }));
      setErrors({});
      onSuccess?.();
    } catch (err) {
      toast.error(err.message || 'Unable to submit enquiry.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={compact ? 'space-y-4 text-left' : 'space-y-5 text-left'} onSubmit={submit}>
      <div className="rounded-[28px] border border-[#dae1ee] bg-[#f7f8fb] p-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Package summary</p>
        <div className={packageSummaryClass}>
          {packageFacts.map((item) => (
            <div
              key={item.label}
              className={`${packageSummaryItemClass} ${
                !compact && item.label === 'Package' ? 'sm:col-span-2 xl:col-span-1' : ''
              }`}
            >
              <div className={compact ? 'flex items-start justify-between gap-4' : ''}>
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400">
                  {item.label}
                </p>
                {compact && item.label === 'Starting price' ? (
                  <span className="rounded-full bg-[#f4ead8] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8a6428]">
                    From
                  </span>
                ) : null}
              </div>
              <p className={packageSummaryValueClass}>
                {item.value}
              </p>
            </div>
          ))}
        </div>
      </div>

      {Object.keys(errors).length ? (
        <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-xs text-rose-700">
          {Object.values(errors).map((value) => (
            <div key={value}>{value}</div>
          ))}
        </div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Full name</label>
          <input
            className={fieldClassName}
            placeholder="Your full name"
            value={form.fullName}
            onChange={(event) => updateField('fullName', event.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Email</label>
          <input
            type="email"
            className={fieldClassName}
            placeholder="you@example.com"
            value={form.email}
            onChange={(event) => updateField('email', event.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Phone</label>
          <input
            className={fieldClassName}
            placeholder="+91 98765 43210"
            value={form.phone}
            onChange={(event) => updateField('phone', event.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Expected travel date</label>
          <input
            type="date"
            className={fieldClassName}
            value={form.travelDate}
            onChange={(event) => updateField('travelDate', event.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Travellers</label>
          <input
            type="number"
            min="1"
            className={fieldClassName}
            placeholder="2"
            value={form.travellers}
            onChange={(event) => updateField('travellers', event.target.value)}
          />
        </div>
        <div>
          <label className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Approx. budget</label>
          <input
            type="number"
            min="0"
            className={fieldClassName}
            placeholder="50000"
            value={form.budget}
            onChange={(event) => updateField('budget', event.target.value)}
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Message</label>
        <textarea
          rows={compact ? 4 : 5}
          className={fieldClassName}
          placeholder="Share your travel preferences, stay style, celebration plans, or any special requests."
          value={form.message}
          onChange={(event) => updateField('message', event.target.value)}
        />
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3">
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600"
          >
            Cancel
          </button>
        ) : null}
        <button
          type="submit"
          disabled={submitting}
          className="rounded-full bg-[#081b3a] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.22em] text-white transition hover:bg-[#0f766e] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Submitting...' : labels.enquire || 'Enquire now'}
        </button>
      </div>
    </form>
  );
};

export default PackageEnquiryForm;
